import React, { FC, useState, useEffect, ReactNode } from 'react';
import { Container } from '@src/components/Container';
import { ContainerType } from '@src/components/Container/enums/containerType.enum';
import { useCurrentRecruiterForEditProfileQuery, useUpdateRecruiterMutation } from '@src/graphql/generated';
import { TextField } from '@src/components/TextField';
import { Button } from '@src/components/Button';
import MediaUpload from '@src/components/MediaUpload';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { useAppDispatch } from '@src/redux';
import { RecruiterUpdateInputLocalState } from './interfaces/recruiterUpdateInput.interface';
import { RecruiterUpdateFields } from './enums/recruiterUpdateFields.enum';
import './styles.scss';

export const EditProfile: FC = () => {
  const [formData, setFormData] = useState<RecruiterUpdateInputLocalState>({
    avatar: undefined,
    fullName: '',
    phoneNumber: '',
    positionTitle: '',
  });
  const dispatch = useAppDispatch();

  const [updateRecruiter, { loading: isUpdateRecruiterLoading }] = useUpdateRecruiterMutation({
    onCompleted: () => {
      dispatch(setSuccessFlashMessage('Profile successfully updated.'));
    },
    onError: () => {
      dispatch(setErrorFlashMessage("Couldn't manage to update profile data, please try later"));
    },
  });

  const { data: recruiterData, loading: isRecruiterQueryLoading } = useCurrentRecruiterForEditProfileQuery();
  const recruiter = recruiterData?.currentRecruiter;

  useEffect(() => {
    if (recruiterData?.currentRecruiter) {
      setFormData({
        avatar: recruiter?.avatar?.fullUrl
          ? {
              id: recruiter?.avatar?.id,
              url: recruiter?.avatar?.fullUrl,
            }
          : undefined,
        fullName: recruiter?.fullName || '',
        phoneNumber: recruiter?.phone || '',
        positionTitle: recruiter?.title || '',
      });
    }
  }, [recruiterData]);

  const onChange = (key: RecruiterUpdateFields, value?: string) => {
    setFormData({ ...formData, [key]: value });
  };

  const onUpdate = () => {
    updateRecruiter({
      variables: {
        updateInput: {
          avatarId: formData.avatar?.id,
          fullName: formData.fullName ? formData.fullName.trim() : undefined,
          phoneNumber: formData.phoneNumber,
          positionTitle: formData.positionTitle,
        },
      },
    });
  };

  const renderMediaUploadCopy = (): ReactNode => {
    return (
      <div className="user-profile-edit__avatar-copy">
        <h4>Upload avatar</h4>
        <p>max 10mb</p>
      </div>
    );
  };

  if (isRecruiterQueryLoading) {
    return null;
  }

  return (
    <Container className="user-profile-edit fade-in" type={ContainerType.BORDERED}>
      <div className="user-profile-edit__form-wrapper">
        <MediaUpload
          canDelete={false}
          className="user-profile-edit__avatar-wrapper"
          file={formData.avatar}
          height={60}
          isCopyAndStatusOutsideDropZone={true}
          mediaUploadCopy={renderMediaUploadCopy()}
          mediaWrapperClassName="user-profile-edit__avatar"
          minHeight={256}
          minWidth={256}
          onRemove={() => {
            onChange(RecruiterUpdateFields.AVATAR, undefined);
          }}
          onUpload={image => {
            onChange(RecruiterUpdateFields.AVATAR, { ...image, url: image.fullUrl });
          }}
          single
          singleMediaCopyWrapperClassName="user-profile-edit__avatar-copy-wrapper"
          titleSize="XS"
          width={60}
        />
        <TextField
          defaultValue={formData.fullName}
          fullWidth={true}
          label="Full name"
          onChange={value => onChange(RecruiterUpdateFields.FULL_NAME, value)}
          placeholder="Full name"
          type="text"
          wrapperClassName="user-profile-edit__input"
        />
        <TextField
          defaultValue={formData.positionTitle}
          fullWidth={true}
          label="Position title"
          onChange={value => onChange(RecruiterUpdateFields.POSITION_TITLE, value)}
          placeholder="Position title"
          type="text"
          wrapperClassName="user-profile-edit__input"
        />
        <TextField
          defaultValue={formData.phoneNumber}
          fullWidth={true}
          label="Phone number"
          onChange={value => onChange(RecruiterUpdateFields.PHONE_NUMBER, value)}
          placeholder="Phone number"
          type="text"
          wrapperClassName="user-profile-edit__input"
        />
        <Button
          className="login-step__continue-button"
          isDisabled={isRecruiterQueryLoading || isUpdateRecruiterLoading}
          onClick={onUpdate}
        >
          Update
        </Button>
      </div>
    </Container>
  );
};
